import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { tv } from "tailwind-variants";

import { PRODUCT_TYPE_LABELS } from "@/domains/profile/models/constants";
import type { HpFeedbackForm, VerifiedProduct } from "@/domains/profile/models/types";
import { resetAnswerValue } from "@/domains/profile/utils/formsort";
import { Button, Checkbox, Divider, Heading } from "@/ui";

const productSelectionStepVariants = tv({
  slots: {
    container: "flex flex-col gap-6",
    productList: "flex flex-col gap-3",
    checkboxLabel:
      "flex cursor-pointer items-center gap-3 rounded-lg border border-line bg-surface p-4",
    productImage: "size-20 rounded-xl object-cover",
  },
  variants: {
    selected: {
      true: { checkboxLabel: "border-accent-line bg-accent-surface" },
    },
  },
});

const { checkboxLabel, container, productImage, productList } = productSelectionStepVariants();

type ProductSelectionStepProps = {
  onContinue: () => void;
  products: VerifiedProduct[];
};

export const ProductSelectionStep = ({ onContinue, products }: ProductSelectionStepProps) => {
  const {
    clearErrors,
    control,
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = useFormContext<HpFeedbackForm>();
  const skipped = watch("skipped");
  const selectedProducts = watch("selectedProducts");

  useEffect(() => {
    if (skipped) {
      setValue("selectedProducts", []);
    }
  }, [skipped, setValue]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      setValue("skipped", false);
    }
  }, [selectedProducts, setValue]);

  const canContinue = skipped || selectedProducts.length > 0;

  const handleContinue = async () => {
    const valid = await trigger(["selectedProducts", "skipped"]);

    if (!valid) {
      return;
    }

    clearErrors();

    if (skipped) {
      resetAnswerValue({ skipped: true, hp_selected_products: [], hp_product_feedbacks: [] });

      return;
    }
    onContinue();
  };

  return (
    <div className={container()}>
      <Heading className="text-4xl sm:text-4xl" level={2}>
        We see you&apos;ve already been using our products. Nice.
      </Heading>
      <p>
        For best results, an EO care plan typically includes up to 3 products. Tell us which of your
        current products are working well for you already and we&apos;ll build them into your plan.
      </p>

      <div className="flex flex-col gap-2">
        <p className="font-semibold">Choose up to 3 to keep in your plan.</p>
        <Controller
          control={control}
          name="selectedProducts"
          render={({ field }) => {
            return (
              <div className={productList()}>
                {products.map((product) => {
                  const checked = field.value.includes(product.sku);

                  return (
                    <label className={checkboxLabel({ selected: checked })} key={product.sku}>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(val) => {
                          field.onChange(
                            val
                              ? [...field.value, product.sku]
                              : field.value.filter((id) => {
                                  return id !== product.sku;
                                }),
                          );
                        }}
                      />
                      <img alt={product.name} className={productImage()} src={product.url} />
                      <span className="font-medium">
                        {product.name}{" "}
                        <span className="capitalize">
                          {PRODUCT_TYPE_LABELS[product.productType] ?? product.productType}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            );
          }}
        />
      </div>

      <Divider.Root>
        <Divider.Line className="border-line" />
        <Divider.Label className="text-sm text-content-secondary">OR</Divider.Label>
        <Divider.Line className="border-line" />
      </Divider.Root>

      <Controller
        control={control}
        name="skipped"
        render={({ field }) => {
          return (
            <label className={checkboxLabel({ selected: field.value })}>
              <Checkbox
                checked={field.value}
                onCheckedChange={(val) => {
                  return field.onChange(!!val);
                }}
              />
              <span className="font-medium">
                I&apos;m not sure. Use EO&apos;s clinical recommendations.
              </span>
            </label>
          );
        }}
      />

      {Object.values(errors).map((error, index) => {
        return (
          <p className="text-sm text-danger" key={index}>
            {error.message?.toString()}
          </p>
        );
      })}

      <Button className="w-full" disabled={!canContinue} onClick={handleContinue} size="lg">
        Continue
      </Button>
    </div>
  );
};
